/**
 * 「自動実行ジョブ」 section for the project Settings page.
 *
 * Renders one row per `scheduledJobs` doc with:
 *   - name + description
 *   - cron expression (editable inline) + humanized form
 *   - last run badge (success / failed / partial + relative time)
 *   - next run time (when set)
 *   - enabled toggle
 *   - 「今すぐ実行」 button (manual kick via `runScheduledJobNow`)
 *   - 「履歴を見る」 button → `<ScheduledJobHistory>` modal panel
 *
 * Layout deliberately mirrors the surrounding Settings sections (`card
 * p-4`, member-row striping) so the page stays visually coherent.
 *
 * Edit gating: `canEdit` (owner/admin) controls toggle + cron + manual
 * kick. Members in lower roles still see the whole table read-only —
 * one of the explicit acceptance criteria.
 */
import { useMemo, useState } from 'react';
import { Play, History as HistoryIcon } from 'lucide-react';
import type { ScheduledJobDoc } from '@mct/shared';
import { humanizeCronExpression } from '@mct/shared';
import { Button } from '../ui/Button';
import { Input, Label } from '../ui/Input';
import { Spinner } from '../ui/Spinner';
import { Badge } from '../ui/Badge';
import { useScheduledJobs } from '../../hooks/useScheduledJobs';
import { getDb } from '../../lib/firebase';
import { updateScheduledJob } from '../../lib/firestore';
import {
  runScheduledJobNow,
  translateCallableError,
} from '../../lib/adminCallables';
import { ScheduledJobHistory } from './ScheduledJobHistory';

function formatRelative(ts: { toDate(): Date } | null | undefined): string {
  if (!ts) return '—';
  const ms = ts.toDate().getTime();
  const diff = Date.now() - ms;
  if (diff < 0) {
    // Future time (e.g. nextRunAt) — render as "in N min".
    const f = -diff;
    if (f < 60_000) return 'まもなく';
    if (f < 3_600_000) return `${Math.round(f / 60_000)} 分後`;
    if (f < 86_400_000) return `${Math.round(f / 3_600_000)} 時間後`;
    return `${Math.round(f / 86_400_000)} 日後`;
  }
  if (diff < 60_000) return '今';
  if (diff < 3_600_000) return `${Math.round(diff / 60_000)} 分前`;
  if (diff < 86_400_000) return `${Math.round(diff / 3_600_000)} 時間前`;
  return `${Math.round(diff / 86_400_000)} 日前`;
}

function statusBadge(s: ScheduledJobDoc['lastStatus']) {
  if (s === 'success') return <Badge tone="green">成功</Badge>;
  if (s === 'failed') return <Badge tone="red">失敗</Badge>;
  if (s === 'partial') return <Badge tone="yellow">部分成功</Badge>;
  return <Badge tone="slate">未実行</Badge>;
}

export function ScheduledJobsSection({
  projectId,
  canEdit,
}: {
  projectId: string;
  canEdit: boolean;
}) {
  const { data: jobs, loading } = useScheduledJobs(projectId);
  const [historyJobId, setHistoryJobId] = useState<string | null>(null);

  const sortedJobs = useMemo(
    () => [...jobs].sort((a, b) => a.jobId.localeCompare(b.jobId)),
    [jobs],
  );

  return (
    <section className="card p-4" data-testid="scheduled-jobs-section">
      <h2 className="text-lg font-semibold">自動実行ジョブ</h2>
      <p className="mt-1 text-xs text-slate-500">
        定期実行ジョブの状態を確認し、cron 式・有効/無効を変更できます。手動キックや実行履歴の確認もここから行えます。
      </p>

      <details className="mt-3 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
        <summary className="cursor-pointer font-medium text-slate-700">
          取得条件と処理内容の詳細
        </summary>
        <div className="mt-2 space-y-3 leading-relaxed">
          <div>
            <div className="font-semibold">Backlog Issue 同期 <code>backlog-sync</code> · 既定 15 分毎</div>
            <p className="mt-0.5">
              連携プロジェクトの直近 issue について Backlog API から最新の
              status / assignee / コメント上位 3 件を取得し、<code>backlogCache</code> Firestore コレクションを更新します。
              既に登録済みの issue だけが対象 (WBS に紐付いてる issueKey が母集団)。
            </p>
          </div>
          <div>
            <div className="font-semibold">Snooze 期限切れ復帰 <code>revive-suggestions</code> · 既定 10 分毎</div>
            <p className="mt-0.5">
              「あとで」で snooze された suggestion の <code>snoozedUntil</code> を確認し、期限切れのものを pending に戻します。
            </p>
          </div>
          <div>
            <div className="font-semibold">日次サマリーメール <code>daily-notifications</code> · 既定 08:00 JST</div>
            <p className="mt-0.5">
              指定時刻にプロジェクトメンバー各人へ「自分が担当の今日のタスク」サマリーをメール送信します。
              送信時刻は上の「日次サマリーメール」セクションで個別に変更可能。
            </p>
          </div>
          <p className="mt-1 text-[11px] text-slate-500">
            ※ いずれも 1 分毎の heartbeat (Cloud Functions onSchedule) が、各ジョブの cron に従って起動します。
            cron 式・有効/無効は下の表で個別変更でき、「今すぐ実行」で手動キックも可能です。
          </p>
        </div>
      </details>

      {loading ? (
        <div className="mt-4 flex justify-center">
          <Spinner />
        </div>
      ) : sortedJobs.length === 0 ? (
        <div className="mt-4 rounded bg-slate-50 px-3 py-2 text-xs text-slate-500">
          このプロジェクトにはまだ自動実行ジョブが登録されていません。
          <br />
          <code>pnpm -F @mct/functions migrate:seed-scheduled-jobs -- --apply</code>{' '}
          を実行してください。
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          {sortedJobs.map((job) => (
            <ScheduledJobRow
              key={job.jobId}
              job={job}
              canEdit={canEdit}
              projectId={projectId}
              onShowHistory={() => setHistoryJobId(job.jobId)}
            />
          ))}
        </div>
      )}

      {historyJobId ? (
        <ScheduledJobHistory
          projectId={projectId}
          jobId={historyJobId}
          onClose={() => setHistoryJobId(null)}
        />
      ) : null}
    </section>
  );
}

function ScheduledJobRow({
  job,
  canEdit,
  projectId,
  onShowHistory,
}: {
  job: ScheduledJobDoc;
  canEdit: boolean;
  projectId: string;
  onShowHistory: () => void;
}) {
  const [cronDraft, setCronDraft] = useState(job.cronExpression);
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const cronChanged = cronDraft.trim() !== job.cronExpression;

  const onSaveCron = async () => {
    if (!cronChanged) return;
    setBusy(true);
    setErr(null);
    setInfo(null);
    try {
      await updateScheduledJob(getDb(), projectId, job.jobId, {
        cronExpression: cronDraft.trim(),
      });
      setInfo('cron を保存しました');
      window.setTimeout(() => setInfo(null), 2500);
    } catch (e) {
      setErr(e instanceof Error ? e.message : '保存に失敗しました');
    } finally {
      setBusy(false);
    }
  };

  const onToggleEnabled = async (next: boolean) => {
    setBusy(true);
    setErr(null);
    try {
      await updateScheduledJob(getDb(), projectId, job.jobId, {
        enabled: next,
      });
    } catch (e) {
      setErr(e instanceof Error ? e.message : '保存に失敗しました');
    } finally {
      setBusy(false);
    }
  };

  const onKickNow = async () => {
    setBusy(true);
    setErr(null);
    setInfo(null);
    try {
      const res = await runScheduledJobNow(projectId, job.jobId);
      if (!res.ok) {
        setErr(res.message);
      } else if (res.outcome.status === 'failed') {
        setErr(`失敗: ${res.outcome.error}`);
      } else {
        setInfo(
          `実行しました（${res.outcome.itemsProcessed ?? 0} 件 / ${res.outcome.durationMs} ms）`,
        );
        window.setTimeout(() => setInfo(null), 4000);
      }
    } catch (e) {
      setErr(translateCallableError(e).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded border border-slate-200 px-3 py-3"
      data-testid={`scheduled-job-row-${job.jobId}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold">{job.name}</h3>
            {statusBadge(job.lastStatus)}
            {!job.enabled ? (
              <Badge tone="slate">無効</Badge>
            ) : null}
          </div>
          <p className="mt-1 text-xs text-slate-500">{job.description}</p>
          <dl className="mt-2 grid grid-cols-1 gap-x-6 gap-y-1 text-xs text-slate-600 sm:grid-cols-2">
            <div className="flex justify-between gap-2">
              <dt className="text-slate-500">最終実行</dt>
              <dd className="tabular-nums">{formatRelative(job.lastRunAt)}</dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt className="text-slate-500">次回実行</dt>
              <dd className="tabular-nums">{formatRelative(job.nextRunAt)}</dd>
            </div>
            {typeof job.lastDurationMs === 'number' ? (
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500">前回所要</dt>
                <dd className="tabular-nums">{job.lastDurationMs} ms</dd>
              </div>
            ) : null}
            {job.lastSummary ? (
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500">前回サマリ</dt>
                <dd className="truncate">{job.lastSummary}</dd>
              </div>
            ) : null}
          </dl>
          {job.lastError ? (
            <div className="mt-2 rounded border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
              <span className="font-mono">{job.lastError}</span>
            </div>
          ) : null}
        </div>

        <div className="flex flex-col items-end gap-2">
          <label className="inline-flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={job.enabled}
              disabled={!canEdit || busy}
              onChange={(e) => onToggleEnabled(e.target.checked)}
              data-testid={`scheduled-job-enabled-${job.jobId}`}
            />
            <span>有効</span>
          </label>
          <Button
            variant="secondary"
            disabled={!canEdit || busy}
            onClick={() => void onKickNow()}
            data-testid={`scheduled-job-run-now-${job.jobId}`}
          >
            <Play className="mr-1 h-3 w-3" /> 今すぐ実行
          </Button>
          <Button
            variant="ghost"
            onClick={onShowHistory}
            data-testid={`scheduled-job-history-${job.jobId}`}
          >
            <HistoryIcon className="mr-1 h-3 w-3" /> 履歴を見る
          </Button>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-[1fr_auto] items-end gap-2">
        <div>
          <Label htmlFor={`cron-${job.jobId}`}>
            cron 式 ({humanizeCronExpression(job.cronExpression)})
          </Label>
          <Input
            id={`cron-${job.jobId}`}
            value={cronDraft}
            onChange={(e) => setCronDraft(e.target.value)}
            disabled={!canEdit || busy}
            placeholder="*/15 * * * *"
            className="font-mono"
          />
        </div>
        <Button
          disabled={!canEdit || busy || !cronChanged}
          onClick={() => void onSaveCron()}
        >
          cron 保存
        </Button>
      </div>

      {info ? (
        <p className="mt-2 text-xs text-emerald-700">{info}</p>
      ) : null}
      {err ? <p className="mt-2 text-xs text-red-600">{err}</p> : null}
    </div>
  );
}
